import React, { useEffect, useState } from 'react'
import { X, XCircle, Download, Clock, Users, Loader2 } from 'lucide-react'
import { sharedReportService } from '../../services/sharedReportService'
import useGlobalCustomerStore from '../../stores/globalCustomerStore'
import { SharedReportListItem } from '../../types/sharedReport'

interface Props {
  isOpen: boolean
  onClose: () => void
  item: SharedReportListItem | null
  customerId: string | null
  onChanged?: () => void
}

const fmtStamp = (iso: string | null): string =>
  iso ? new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—'

const ReportViewer: React.FC<Props> = ({ isOpen, onClose, item, customerId, onChanged }) => {
  const [html, setHtml] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [recipientView, setRecipientView] = useState(false)
  const selectedCustomer = useGlobalCustomerStore((s) => s.selectedCustomer)
  const senderName = selectedCustomer?.customerName ?? 'Your organization'

  useEffect(() => {
    if (!isOpen || !item?.snapshotId) return
    let cancelled = false
    setLoading(true); setHtml(null); setRecipientView(false)
    sharedReportService.getSnapshotHtml(item.snapshotId, customerId ?? '')
      .then((h) => { if (!cancelled) { setHtml(h); setLoading(false) } })
      .catch(() => { if (!cancelled) { setHtml('<p style="padding:24px;font-family:sans-serif;color:#64748b">Could not load this report.</p>'); setLoading(false) } })
    return () => { cancelled = true }
  }, [isOpen, item, customerId])

  useEffect(() => {
    const onEsc = (e: KeyboardEvent) => e.key === 'Escape' && onClose()
    if (isOpen) document.addEventListener('keydown', onEsc)
    return () => document.removeEventListener('keydown', onEsc)
  }, [isOpen, onClose])

  if (!isOpen || !item) return null

  const toolBtn = 'inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold rounded-lg border border-gray-200 bg-white text-gray-600 hover:text-blue-600 hover:border-blue-300 hover:bg-blue-50 transition-colors'

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-6">
      <div className="absolute inset-0 bg-slate-900/55" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-[960px] max-w-full max-h-full flex flex-col overflow-hidden">
        {/* Toolbar */}
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-gray-100">
          <div className="flex items-center gap-3 min-w-0">
            <strong className="text-sm truncate">{item.title}</strong>
            <span className="inline-flex items-center gap-1 text-[11.5px] text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
              <Clock className="h-3 w-3" /> Data as of {fmtStamp(item.dataAsOf)}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button className={toolBtn} onClick={() => setRecipientView((v) => !v)}>
              <Users className="h-3.5 w-3.5" /> {recipientView ? 'Author view' : 'Recipient view'}
            </button>
            {item.status !== 'draft' && item.status !== 'revoked' && item.shareId && (
              <button
                className={`${toolBtn} hover:!text-rose-600 hover:!border-rose-300 hover:!bg-rose-50`}
                onClick={async () => {
                  if (!customerId || !item.shareId) return
                  try { await sharedReportService.revoke(item.shareId, customerId) } finally { onChanged?.(); onClose() }
                }}
              >
                <XCircle className="h-3.5 w-3.5" /> Revoke
              </button>
            )}
            <button
              onClick={() => item.snapshotId && customerId && sharedReportService.downloadPdf(item.snapshotId, customerId, `${item.title.replace(/[^a-z0-9]/gi, '-').toLowerCase()}.pdf`)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg bg-gradient-to-b from-indigo-600 to-indigo-700 text-white hover:brightness-105"
            >
              <Download className="h-3.5 w-3.5" /> Download PDF
            </button>
            <button onClick={onClose} className="h-8 w-8 inline-flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100" aria-label="Close">
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Recipient banner (only in recipient-view preview) */}
        {recipientView && (
          <div className="flex items-center justify-between px-5 py-3 bg-gradient-to-r from-slate-900 to-slate-700 text-white">
            <div className="flex items-center gap-3">
              <img src="/avi-logo-white-crop.png" alt="AVI by Athletiverse" className="h-7 w-auto" />
              <span className="text-sm font-semibold tracking-tight">Athletiverse</span>
              <span className="h-6 w-px bg-white/20" />
              <div>
                <div className="text-sm font-semibold">{senderName}</div>
                <div className="text-[11.5px] text-slate-300">shared this report with you via AVI</div>
              </div>
            </div>
            <button className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg bg-white text-slate-800">
              <Download className="h-3.5 w-3.5" /> Download PDF
            </button>
          </div>
        )}

        {/* Snapshot */}
        <div className="flex-1 bg-slate-100 overflow-hidden">
          {loading || !html ? (
            <div className="h-[520px] flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-indigo-600" /></div>
          ) : (
            <iframe title="Report snapshot" srcDoc={html} sandbox="" className="w-full h-[560px] border-0" />
          )}
        </div>
      </div>
    </div>
  )
}

export default ReportViewer
